import * as Tooltip from '@radix-ui/react-tooltip'
import { ChevronLeft, Pause, Play } from 'lucide-react'
import { useTimer } from './timer-context'

function formatTime(ms: number, roundUp = false): string {
  const negative = ms < 0
  const abs = Math.abs(ms)
  const totalSeconds = roundUp ? Math.ceil(abs / 1000) : Math.floor(abs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const formatted = [
    String(hours).padStart(2, '0'),
    String(minutes).padStart(2, '0'),
    String(seconds).padStart(2, '0'),
  ].join(':')
  return negative ? `-${formatted}` : formatted
}

const tooltipContentClass =
  'border-border bg-background text-foreground z-[100] rounded-md border px-3 py-1.5 text-sm font-medium shadow-md'

export function TimerDisplay() {
  const {
    activeView,
    stopwatch,
    timer,
    hasTimer,
    startStopwatch,
    pauseStopwatch,
    startTimer,
    pauseTimer,
    setActiveView,
  } = useTimer()

  const isStopwatchView = activeView === 'stopwatch'
  // Timer uses ceil rounding, stopwatch uses floor — ensures they always
  // tick in unison (the tick loop applies the same delta to both each interval).
  const displayMs = isStopwatchView ? stopwatch.elapsedMs : timer.remainingMs
  const isRunning = isStopwatchView
    ? stopwatch.status === 'running'
    : timer.status === 'running' || timer.status === 'overage'
  const isOverage = !isStopwatchView && timer.status === 'overage'

  // Colors — always reflect the activeView, not just when running
  const colorClass = isOverage
    ? 'text-red-500'
    : isStopwatchView
      ? 'text-blue-500'
      : 'text-orange-500'

  const bgClass = isOverage
    ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-800'
    : isStopwatchView
      ? 'bg-blue-50/50 border-blue-200 dark:bg-blue-950/20 dark:border-blue-800'
      : 'bg-orange-50/50 border-orange-200 dark:bg-orange-950/20 dark:border-orange-800'

  // Timer expired = paused/idle with exactly zero remaining (not negative/overage)
  const timerExpired = !isStopwatchView && !isRunning && timer.remainingMs === 0

  const handlePlayPause = () => {
    if (isStopwatchView) {
      isRunning ? pauseStopwatch() : startStopwatch()
    } else {
      if (isRunning) {
        pauseTimer()
      } else if (timer.remainingMs !== 0) {
        startTimer(timer.durationMs)
      }
    }
  }

  const handleToggleView = () => {
    setActiveView(isStopwatchView ? 'timer' : 'stopwatch')
  }

  return (
    <Tooltip.Provider delayDuration={150}>
      <div
        className={`group/timer flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${bgClass}`}
      >
        {hasTimer && (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <button
                type="button"
                onClick={handleToggleView}
                aria-label={isStopwatchView ? 'View timer' : 'View stopwatch'}
                className="text-muted-foreground hover:text-foreground p-0.5 transition-all hover:scale-125 hover:font-black"
              >
                <ChevronLeft
                  className={`h-3.5 w-3.5 stroke-[3] transition-all hover:stroke-[4] ${!isStopwatchView ? 'rotate-180' : ''}`}
                />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                side="top"
                align="center"
                className={tooltipContentClass}
              >
                {isStopwatchView ? 'View timer' : 'View stopwatch'}
                <Tooltip.Arrow className="fill-background" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}

        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              onClick={handlePlayPause}
              disabled={timerExpired}
              aria-label={isRunning ? 'Pause' : 'Start'}
              className={`p-0.5 ${timerExpired ? 'text-muted-foreground cursor-not-allowed opacity-50' : colorClass}`}
            >
              {isRunning ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5 group-hover/timer:fill-current" />
              )}
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              align="center"
              className={tooltipContentClass}
            >
              {isRunning ? 'Pause' : 'Start'}{' '}
              {isStopwatchView ? 'stopwatch' : 'timer'}
              <Tooltip.Arrow className="fill-background" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>

        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              onClick={handlePlayPause}
              disabled={timerExpired}
              className={`font-mono text-sm font-semibold tracking-wide ${timerExpired ? 'text-muted-foreground cursor-not-allowed opacity-50' : colorClass}`}
            >
              {formatTime(displayMs, !isStopwatchView)}
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              align="center"
              className={tooltipContentClass}
            >
              {isRunning ? 'Pause' : 'Start'}{' '}
              {isStopwatchView ? 'stopwatch' : 'timer'}
              <Tooltip.Arrow className="fill-background" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>
    </Tooltip.Provider>
  )
}
